import { getConfiguredMapId } from '../stores/GoogleMapState'
import { FeatureMapMarker } from '../types/features'

// Advanced Markers only render on a map initialised with a mapId, so the
// configured Map ID doubles as the rollout flag: configured → Advanced
// Markers, blank → legacy google.maps.Marker (instant no-deploy rollback by
// clearing the CMS field). This adapter keeps that single gate in one place so
// callers never branch on marker type (see route-pins.ts lines 4-8 and
// NZTAJP-565 / docs/ROUTES_API_POST_MIGRATION.md). Remove the legacy branches
// once the Map ID is permanent in production.

type MarkerPosition = google.maps.LatLng | google.maps.LatLngLiteral

export type CreateFeatureMarkerOptions = {
  map: google.maps.Map
  position: MarkerPosition
  iconUrl: string
  iconSizePx?: number
  zIndex?: number
  properties?: any
  onClick?: () => void
}

export type MarkerStyle = {
  iconUrl: string
  iconSizePx: number
  zIndex: number
}

export type CreateClusterBubbleMarkerOptions = {
  position: MarkerPosition
  count: number
  circleColor: string
  textColor: string
  zIndex: number
}

// Advanced markers have no key/value store like legacy markers, so their
// feature properties are held here keyed by the marker instance. Entries are
// released automatically once a marker is garbage-collected.
const advancedMarkerProperties = new WeakMap<google.maps.marker.AdvancedMarkerElement, any>()

const CLUSTER_CIRCLE_SIZE_PX = 35
const CLUSTER_CIRCLE_RADIUS_PX = CLUSTER_CIRCLE_SIZE_PX / 2 // 17.5

// A legacy Icon with an explicit scaledSize when a size is given, otherwise the
// bare URL string so the image keeps its natural dimensions (as
// TrafficUpdateDetailMap relies on).
function buildLegacyIcon(iconUrl: string, iconSizePx?: number): string | google.maps.Icon {
  if (iconSizePx === undefined) {
    return iconUrl
  }
  return {
    url: iconUrl,
    scaledSize: new google.maps.Size(iconSizePx, iconSizePx, 'px', 'px'),
  }
}

function applyImageSize(image: HTMLImageElement, iconUrl: string, iconSizePx?: number): void {
  image.src = iconUrl
  if (iconSizePx !== undefined) {
    image.width = iconSizePx
    image.height = iconSizePx
  }
}

function buildMarkerImage(iconUrl: string, iconSizePx?: number): HTMLImageElement {
  const image = document.createElement('img')
  applyImageSize(image, iconUrl, iconSizePx)
  return image
}

export function createFeatureMarker(options: CreateFeatureMarkerOptions): FeatureMapMarker {
  const { map, position, iconUrl, iconSizePx, zIndex, properties, onClick } = options

  if (getConfiguredMapId()) {
    const advancedOptions: google.maps.marker.AdvancedMarkerElementOptions = {
      position,
      map,
      zIndex,
      content: buildMarkerImage(iconUrl, iconSizePx),
    }
    if (onClick) {
      // AdvancedMarkerElement ignores click events unless it is made clickable.
      advancedOptions.gmpClickable = true
    }
    const marker = new google.maps.marker.AdvancedMarkerElement(advancedOptions)
    if (properties !== undefined) {
      advancedMarkerProperties.set(marker, properties)
    }
    if (onClick) {
      marker.addListener('click', onClick)
    }
    return marker
  }

  const marker = new google.maps.Marker({
    position,
    map,
    icon: buildLegacyIcon(iconUrl, iconSizePx),
    zIndex,
  })
  if (properties !== undefined) {
    marker.set('properties', properties)
  }
  if (onClick) {
    marker.addListener('click', onClick)
  }
  return marker
}

export function getMarkerProperties(marker: FeatureMapMarker): any | undefined {
  if (marker instanceof google.maps.Marker) {
    return marker.get('properties')
  }
  return advancedMarkerProperties.get(marker)
}

// The active map item reaches components as one of three shapes: a marker, a
// data-layer feature (properties behind getProperty), or a plain GeoJSON
// feature. Reading `.properties` directly only ever worked because a legacy
// marker's set('properties', ...) also exposed it as an own property; an
// Advanced Marker keeps it in the WeakMap above, so `.properties` is undefined.
// Resolve through here instead of branching at the call site.
export function getFeatureProperty(item: any, key: string): any {
  if (!item) {
    return undefined
  }
  if (typeof item.getProperty === 'function') {
    return item.getProperty(key)
  }
  return getMarkerProperties(item)?.[key] ?? item.properties?.[key]
}

// Normalises the various position shapes the Maps API returns (a LatLng exposes
// lat()/lng() methods; a literal exposes lat/lng number properties) into a plain
// literal, or null when there is no position.
function toLatLngLiteral(
  position: google.maps.LatLng | google.maps.LatLngLiteral | google.maps.LatLngAltitude | null | undefined,
): google.maps.LatLngLiteral | null {
  if (!position) {
    return null
  }
  if (typeof (position as google.maps.LatLng).lat === 'function') {
    const latLng = position as google.maps.LatLng
    return { lat: latLng.lat(), lng: latLng.lng() }
  }
  const literal = position as google.maps.LatLngLiteral
  return { lat: literal.lat, lng: literal.lng }
}

export function getMarkerPosition(marker: FeatureMapMarker): google.maps.LatLngLiteral | null {
  if (marker instanceof google.maps.Marker) {
    return toLatLngLiteral(marker.getPosition())
  }
  return toLatLngLiteral(marker.position)
}

export function setMarkerStyle(marker: FeatureMapMarker, { iconUrl, iconSizePx, zIndex }: MarkerStyle): void {
  if (marker instanceof google.maps.Marker) {
    marker.setIcon({
      url: iconUrl,
      scaledSize: new google.maps.Size(iconSizePx, iconSizePx, 'px', 'px'),
    })
    marker.setZIndex(zIndex)
    return
  }

  if (marker.content instanceof HTMLImageElement) {
    applyImageSize(marker.content, iconUrl, iconSizePx)
  } else {
    marker.content = buildMarkerImage(iconUrl, iconSizePx)
  }
  marker.zIndex = zIndex
}

export function removeFeatureMarker(marker: FeatureMapMarker | null | undefined): void {
  if (!marker) return
  if (marker instanceof google.maps.Marker) {
    marker.setMap(null)
  } else {
    // AdvancedMarkerElement has no setMap(); detach via the map property.
    marker.map = null
  }
}

function buildClusterCircleSvg(circleColor: string, textColor: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${CLUSTER_CIRCLE_SIZE_PX}" height="${CLUSTER_CIRCLE_SIZE_PX}" viewBox="0 0 ${CLUSTER_CIRCLE_SIZE_PX} ${CLUSTER_CIRCLE_SIZE_PX}"><circle cx="${CLUSTER_CIRCLE_RADIUS_PX}" cy="${CLUSTER_CIRCLE_RADIUS_PX}" r="${CLUSTER_CIRCLE_RADIUS_PX}" fill="${circleColor}" stroke="${textColor}" stroke-width="1"/></svg>`
}

function clusterCircleDataUri(circleColor: string, textColor: string): string {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(buildClusterCircleSvg(circleColor, textColor))}`
}

function buildClusterCircleImage(circleColor: string, textColor: string): HTMLImageElement {
  const image = document.createElement('img')
  image.src = clusterCircleDataUri(circleColor, textColor)
  image.width = CLUSTER_CIRCLE_SIZE_PX
  image.height = CLUSTER_CIRCLE_SIZE_PX
  return image
}

function buildClusterCountLabel(count: number, textColor: string): HTMLElement {
  const label = document.createElement('span')
  label.textContent = String(count)
  label.style.position = 'absolute'
  label.style.top = '50%'
  label.style.left = '50%'
  label.style.transform = 'translate(-50%, -50%)'
  label.style.color = textColor
  label.style.fontSize = '14px'
  label.style.fontWeight = '600'
  return label
}

function buildClusterBubbleContent(count: number, circleColor: string, textColor: string): HTMLElement {
  const wrapper = document.createElement('div')
  wrapper.style.position = 'relative'
  wrapper.style.width = `${CLUSTER_CIRCLE_SIZE_PX}px`
  wrapper.style.height = `${CLUSTER_CIRCLE_SIZE_PX}px`
  // Advanced markers anchor at their bottom-centre, but the legacy cluster icon
  // was anchored at its centre. Shift the bubble down by half its height so the
  // circle stays centred on the cluster position.
  wrapper.style.transform = 'translateY(50%)'
  wrapper.appendChild(buildClusterCircleImage(circleColor, textColor))
  wrapper.appendChild(buildClusterCountLabel(count, textColor))
  return wrapper
}

// Built without a map — the MarkerClusterer attaches the returned marker itself.
export function createClusterBubbleMarker(options: CreateClusterBubbleMarkerOptions): FeatureMapMarker {
  const { position, count, circleColor, textColor, zIndex } = options

  if (getConfiguredMapId()) {
    return new google.maps.marker.AdvancedMarkerElement({
      position,
      zIndex,
      // Keep the clusterer's zoom-on-click behaviour working.
      gmpClickable: true,
      content: buildClusterBubbleContent(count, circleColor, textColor),
    })
  }

  return new google.maps.Marker({
    position,
    icon: {
      url: clusterCircleDataUri(circleColor, textColor),
      anchor: new google.maps.Point(CLUSTER_CIRCLE_RADIUS_PX, CLUSTER_CIRCLE_RADIUS_PX),
      labelOrigin: new google.maps.Point(CLUSTER_CIRCLE_RADIUS_PX, CLUSTER_CIRCLE_RADIUS_PX),
    },
    label: { text: String(count), color: textColor, fontSize: '14px', fontWeight: '600' },
    zIndex,
  })
}
